import { c as _c } from "react/compiler-runtime";
/**
 * useIntentPrefetch — stable hover/focus/touch handlers for predictive prefetch.
 *
 * Returns a `{ onMouseEnter, onFocus, onPointerDown }` triple to spread onto
 * any clickable element. Each handler fires the supplied `prefetch()` callback;
 * idempotency comes from TanStack's `prefetchQuery` cache (the same path used
 * by every codegen-emitted `usePrefetch<Name>` hook), so repeated hovers don't
 * trigger duplicate network requests.
 *
 * Usage:
 *
 *     const prefetchMarket = usePrefetchGetMarket();
 *     const intent = useIntentPrefetch(() => {
 *       void prefetchMarket({ pubkey });
 *     });
 *     return <Link href={`/market/${pubkey}`} {...intent}>...</Link>;
 *
 * Inside `<Items>`, prefer the runtime's `onRowHover` callback over calling
 * this hook per row — the runtime builds one stable `intent` triple per row
 * id and hands it to the renderer (`{ ref, id, intent }`).
 *
 * Handler identities are stable across renders so spreading them does not
 * cause downstream re-renders on prop-identity diffs. The `prefetch`
 * callback identity is allowed to change every render — the latest closure
 * is captured via a ref and read at event-fire time (useEvent semantics).
 */
import { useCallback, useMemo } from 'react';
import { useLatestRef } from './useLatestRef.js';
export function useIntentPrefetch(prefetch) {
  const $ = _c(4);
  const latest = useLatestRef(prefetch);
  let t0;
  if ($[0] !== latest) {
    t0 = () => {
      latest.current();
    };
    $[0] = latest;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const fire = t0;
  let t1;
  if ($[2] !== fire) {
    t1 = {
      onMouseEnter: fire,
      onFocus: fire,
      onPointerDown: fire
    };
    $[2] = fire;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}